import { Injectable } from '@angular/core';
import {
    FixedAssetsSchedule,
    FixedAssetsScheduleMovement,
    FixedAssetsScheduleReferenceType,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME } from './fixed-assets-schedule.service';

import { isFli, isGroup, isSubfli } from '@modeso/aumico-lib-templates-fe';

@Injectable()
export class FixedAssetsScheduleValidationService {
    isFixedAssetsScheduleDataValidateAgainstMapping(
        fixedAssetScheduleData: FixedAssetsSchedule,
        mappingData: IAccountMappingTemplate,
    ): boolean {
        if (!mappingData) {
            return false;
        }
        let movements = [];
        if (fixedAssetScheduleData && fixedAssetScheduleData.movements && fixedAssetScheduleData.movements.length > 0) {
            movements = fixedAssetScheduleData.movements.filter(
                (movement) => movement.name != DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME,
            );
        }
        if (fixedAssetScheduleData.referenceType === FixedAssetsScheduleReferenceType.GROUP) {
            const foundGroup = this.searchForReferenceGroup(fixedAssetScheduleData.reference, [mappingData.group]);
            if (foundGroup == null) {
                return false;
            }

            this.findAllNames(foundGroup);
            return this.validateExistenceAndOrder(movements, foundGroup);
        }
        // CASE: User chooses FLI
        if (fixedAssetScheduleData.referenceType === FixedAssetsScheduleReferenceType.FLI) {
            const foundFli = this.searchForReferenceFli(fixedAssetScheduleData.reference, [mappingData.group]);

            if (foundFli == null) {
                return false;
            }

            this.findAllNames(foundFli);
            return this.validateExistenceAndOrder(movements, foundFli);
        }

        // CASE: User chooses SUB FLI or OF THAT ITEM
        if (
            fixedAssetScheduleData.referenceType === FixedAssetsScheduleReferenceType.SUB_FLI ||
            fixedAssetScheduleData.referenceType === FixedAssetsScheduleReferenceType.OF_THAT_ITEM
        ) {
            const foundSubfli = this.searchForReferenceSubFli(fixedAssetScheduleData.reference, [mappingData.group]);

            return foundSubfli != null;
        }

        return false;
    }

    private searchForReferenceGroup(
        reference: string,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingTemplateGroup | undefined {
        for (let group of templateGroups) {
            if (group.name === reference) {
                return group;
            }

            let nestedGroup = this.searchForReferenceGroup(reference, group.groups);
            if (nestedGroup != null) {
                return nestedGroup;
            }
        }
        return undefined;
    }

    private searchForReferenceFli(
        reference: string,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingTemplateGroupItemFLI | undefined {
        for (let group of templateGroups) {
            // First, check the items at the current group level
            for (let item of group.items) {
                if (item.name === reference) {
                    return item as IAccountMappingTemplateGroupItemFLI;
                }
            }

            // If not found at current level, search within nested groups
            let nestedItem = this.searchForReferenceFli(reference, group.groups);
            if (nestedItem) {
                return nestedItem;
            }
        }
        return null;
    }

    private searchForReferenceSubFli(
        reference: string,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingSubFLI | undefined {
        for (let group of templateGroups) {
            // Iterate over items
            for (let item of group.items) {
                for (let account of (item as IAccountMappingTemplateGroupItemFLI).accounts) {
                    if (
                        (account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) &&
                        account.name === reference
                    ) {
                        return account;
                    }
                }
            }

            // If not found at current level, search within nested groups
            let nestedSubFLI = this.searchForReferenceSubFli(reference, group.groups);
            if (nestedSubFLI) {
                return nestedSubFLI;
            }
        }
        return undefined;
    }

    private findAllNames(
        node: IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
    ): string[] {
        if (isGroup(node as SortedMappingItem) && (node as IAccountMappingTemplateGroup).sortedItems != null) {
            return this.findAllNamesInSortedItems(
                (node as IAccountMappingTemplateGroup).sortedItems as SortedMappingItem[],
            );
        }

        let names: string[] = [];

        if ('accounts' in node) {
            for (let account of node.accounts) {
                if (isSubfli(account)) {
                    names.push(account.name);
                }
            }
        }

        return names;
    }

    private findAllNamesInSortedItems(sortedItems: SortedMappingItem[]): string[] {
        const names: string[] = [];

        for (const sortedItem of sortedItems) {
            if (isGroup(sortedItem)) {
                if (!sortedItem.isHiddenGroup) {
                    names.push(sortedItem.name);
                }

                if (sortedItem.sortedItems != null) {
                    names.push(...this.findAllNamesInSortedItems(sortedItem.sortedItems as SortedMappingItem[]));
                }
            } else if (isFli(sortedItem)) {
                names.push(sortedItem.name);

                for (let account of (sortedItem as IAccountMappingTemplateGroupItemFLI).accounts) {
                    if (isSubfli(account)) {
                        names.push(account.name);
                    }
                }
            }
        }

        return names;
    }

    private validateExistenceAndOrder(
        flatArray: FixedAssetsScheduleMovement[],
        nestedObject: IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
    ): boolean {
        const nestedNames = this.findAllNames(nestedObject);
        if (flatArray.length !== nestedNames.length) {
            return false;
        }

        for (let i = 0; i < flatArray.length; i++) {
            if (nestedNames[i] != flatArray[i].name) {
                return false;
            }
        }

        return true;
    }
}
